import React, { useState, useEffect } from 'react';

interface WaitForPageSettleProps {
  loadingPlaceholder?: React.ReactNode | null;
  timeout?: number; // Fallback timeout in milliseconds (default: 6000)
  children: React.ReactNode;
}

export const WaitForPageSettle: React.FC<WaitForPageSettleProps> = ({
  loadingPlaceholder,
  timeout = 6000,
  children,
}) => {
  const [isPageSettled, setIsPageSettled] = useState(false);

  useEffect(() => {
    // Check if page is already loaded
    if (document.readyState === 'complete') {
      // Use requestIdleCallback to wait for browser to be idle
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          () => {
            setIsPageSettled(true);
          },
          { timeout }, // Fallback timeout
        );
      } else {
        // Fallback for browsers that don't support requestIdleCallback
        setTimeout(() => {
          setIsPageSettled(true);
        }, timeout);
      }

      return () => {};
    }

    // Wait for load event
    const handleLoad = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          () => {
            setIsPageSettled(true);
          },
          { timeout },
        );
      } else {
        setTimeout(() => {
          setIsPageSettled(true);
        }, timeout);
      }
    };

    window.addEventListener('load', handleLoad);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [timeout]);

  // Show loading placeholder if it exists and page is not settled
  if (
    !isPageSettled &&
    loadingPlaceholder !== null &&
    loadingPlaceholder !== undefined
  ) {
    return <>{loadingPlaceholder}</>;
  }

  // Don't render children until page is settled
  if (!isPageSettled) {
    return null;
  }

  // Render children after page settles
  return <>{children}</>;
};
